// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/MultiTicketBrowseRail.test.tsx
import {
  type AskPunttAttachment,
  type AskPunttProjectData,
} from "@mg/schemas/src/christo/askPuntt";
import { useTranslation } from "react-i18next";

import { ChatArtifactsPanel } from "./ChatArtifactsPanel";
import { ChatDetailsPanel } from "./ChatDetailsPanel";

import {
  type ChatArtifact,
  type ChatArtifactOpenPayload,
} from "../artifactTypes";
import { PROJECT_RAIL_WIDTH_PX } from "../utils/projectWorkspaceLayout";
import { type UsedConnector } from "../utils/usedConnectors";

/**
 * Fixed-width right rail for Ask Puntt cowork-style workspace.
 * Reviews/Documents size to their content (with a min height and max cap);
 * Chat Details fills the remaining space below.
 */
export function ChatWorkspaceRail({
  reviewArtifacts,
  canvasArtifacts,
  isArtifactActive,
  onOpenArtifact,
  isArtifactUnread,
  title,
  workflowType,
  description,
  findingsSummary,
  ticketFindings,
  uploadedFiles,
  usedConnectors,
  reviewsStatus,
  isKillerFlowStreaming,
  hasActiveStage,
  detailsOpen,
  onDetailsOpenChange,
  railTestId = "chat-artifacts-rail",
}: {
  reviewArtifacts: readonly ChatArtifact[];
  canvasArtifacts: readonly ChatArtifact[];
  isArtifactActive: (artifact: ChatArtifact) => boolean;
  onOpenArtifact: (payload: ChatArtifactOpenPayload) => void;
  isArtifactUnread?: (artifact: ChatArtifact) => boolean;
  title: string;
  workflowType?: string;
  description?: string;
  findingsSummary?: string;
  ticketFindings?: AskPunttProjectData["ticketFindings"];
  uploadedFiles?: readonly AskPunttAttachment[];
  usedConnectors?: readonly UsedConnector[];
  reviewsStatus?: string;
  isKillerFlowStreaming?: boolean;
  hasActiveStage?: boolean;
  detailsOpen: boolean;
  onDetailsOpenChange: (open: boolean) => void;
  railTestId?: string;
}) {
  const { t } = useTranslation("ui");
  const hasArtifacts = reviewArtifacts.length > 0 || canvasArtifacts.length > 0;

  return (
    <aside
      className="box-border flex h-full min-h-0 shrink-0 flex-col gap-3 overflow-hidden border-l border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 p-3"
      style={{
        width: PROJECT_RAIL_WIDTH_PX,
        minWidth: PROJECT_RAIL_WIDTH_PX,
        maxWidth: PROJECT_RAIL_WIDTH_PX,
        flexBasis: PROJECT_RAIL_WIDTH_PX,
      }}
      data-testid={railTestId}
      aria-label={t("chatWorkspaceSidebar")}
    >
      {hasArtifacts ? (
        <div
          className="flex max-h-[55%] min-h-32 shrink-0 flex-col overflow-y-auto overscroll-contain [scrollbar-width:thin]"
          data-testid="chat-artifacts-rail-slot"
        >
          <ChatArtifactsPanel
            reviewArtifacts={reviewArtifacts}
            canvasArtifacts={canvasArtifacts}
            isArtifactActive={isArtifactActive}
            onOpenArtifact={onOpenArtifact}
            isArtifactUnread={isArtifactUnread}
            reviewsStatus={reviewsStatus}
          />
        </div>
      ) : null}
      <div
        className={`flex flex-col overflow-hidden ${
          detailsOpen ? "min-h-0 flex-1" : "shrink-0"
        }`}
        data-testid="chat-details-rail-slot"
        data-section-expanded={detailsOpen ? "true" : "false"}
      >
        <ChatDetailsPanel
          title={title}
          workflowType={workflowType}
          description={description}
          findingsSummary={findingsSummary}
          ticketFindings={ticketFindings}
          uploadedFiles={uploadedFiles}
          usedConnectors={usedConnectors}
          isKillerFlowStreaming={isKillerFlowStreaming}
          hasActiveStage={hasActiveStage}
          panelOpen={detailsOpen}
          onPanelOpenChange={onDetailsOpenChange}
        />
      </div>
    </aside>
  );
}

/** @deprecated Use ChatWorkspaceRail — kept as alias during migration. */
export { ChatWorkspaceRail as MultiTicketBrowseRail };
